import { OrgAddressFilterTypesEnum } from '../constants/addressBook';
import { CUSTOM } from '../constants/campaigns';
import {
  EMAIL_MESSAGE,
  EMAIL_SUBJECT,
  EMAIL_TEMPLATE_ID,
  MESSAGE,
  NOTE_TEMPLATE_ID,
  RECEIVER_EMAIL,
} from '../constants/templates';
import { IOrgAddress } from '../types/addressBook';
import { isMarketplaceBucketItem, TMarketplaceBucketItem } from '../types/bucket';
import { ICampaign, ICampaignItem } from '../types/campaigns';
import { IEffectiveCreateSendCustomMessage } from '../types/createSendCustomMessage';
import { IEffectiveCreateSendEmail } from '../types/createSendEmail';
import { ShippingModeEnum, TMultiKitAddressMode } from '../types/createSendRecipients';
import { IOnDemandItem } from '../types/inventories';
import { IGiftBudget } from '../types/marketplace';
import { ICommonAddress } from '../types/shipping';
import { IAddEmailFormValues, IAddNoteFormValues } from '../types/templates';
import { normalizeStateCode } from './addressValidation';
import { isDigitalBox } from './campaigns';
import { isItemDigital } from './inventories';
import { resolveLooseContentsEngagementId, splitBucketBySource } from './marketplacePayload';
import { mapOrgAddressToCommonAddress } from './oneLink';
import { orderPygOptionIds } from './pygOptions';

export type TLinkContentItem = ICampaignItem | IOnDemandItem | TMarketplaceBucketItem;

export interface ILinkMarketplaceContents {
  isCustomCart: boolean;
  giftBudget: IGiftBudget | null;
}

export interface ILinkContentsCandidate {
  pre_created_engagement_id: string;
  campaign_name: string;
  items: (ICampaignItem | IOnDemandItem)[];
  pick_campaigns?: ICampaign[];
  pick_campaign_ids?: string[];
  marketplace_items?: TMarketplaceBucketItem[] | null;
  gift_budget?: IGiftBudget | null;
}

export interface ILinkShippingCandidate {
  status: string;
  is_receiver_address_fixed: boolean;
  is_office_dropship_enabled: boolean;
  org_address_ids?: string[];
  receiver_fixed_address?: ICommonAddress;
}

/**
 * Splits contents into request-shaped pieces. Marketplace picks never travel in `items[]`; the
 * branch decides whether the id also joins `pick_campaign_ids`:
 *
 * - custom cart → map only, no id reference (variant 4.8)
 * - pick-your-gift options → map plus id reference (variants 4.2/4.3/4.6)
 */
export const mapLinkContents = (
  kits: ICampaign[],
  items: TLinkContentItem[],
  preCreatedBoxId: string | undefined,
  marketplace?: ILinkMarketplaceContents,
  pygOptionOrder: string[] = [],
): ILinkContentsCandidate => {
  const { ieItems, marketplaceItems } = splitBucketBySource<ICampaignItem | IOnDemandItem>(items);

  // One kit beside one pick is a two-option send (variant 4.2), so it must take the
  // `pick_campaigns` branch — the single-kit branch would emit that kit's items too.
  const pygPicks = marketplace?.isCustomCart ? [] : marketplaceItems;

  const giftBudget = marketplace?.giftBudget ?? null;
  const hasMarketplaceContext = marketplaceItems.length > 0 || giftBudget !== null;

  const marketplaceFields: Partial<ILinkContentsCandidate> = hasMarketplaceContext
    ? {
        marketplace_items: marketplaceItems.length ? marketplaceItems : null,
        gift_budget: giftBudget,
        pick_campaign_ids: [],
      }
    : {};

  if (kits.length === 1 && pygPicks.length === 0) {
    const kit = kits[0];
    return {
      pre_created_engagement_id: kit.box_id,
      campaign_name: kit.name,
      items: kit.items ?? [],
      ...marketplaceFields,
    };
  }

  if (kits.length + pygPicks.length > 1) {
    return {
      pre_created_engagement_id: '',
      campaign_name: '',
      items: [],
      pick_campaigns: kits,
      ...marketplaceFields,
      pick_campaign_ids: orderPygOptionIds(kits, pygPicks, pygOptionOrder),
    };
  }

  return {
    pre_created_engagement_id: resolveLooseContentsEngagementId(preCreatedBoxId, ieItems.length, hasMarketplaceContext),
    campaign_name: '',
    items: ieItems,
    ...marketplaceFields,
  };
};

export const isLinkContentDigital = (items: TLinkContentItem[]): boolean =>
  items.length > 0 && items.every((item) => !isMarketplaceBucketItem(item) && isItemDigital(item));

export const isLinkContentsDigital = (contents: ILinkContentsCandidate): boolean =>
  contents.pick_campaigns?.length ? contents.pick_campaigns.every(isDigitalBox) : isLinkContentDigital(contents.items);

export const mapLinkFixedAddress = (address: IOrgAddress | null): ICommonAddress | undefined => {
  const common = mapOrgAddressToCommonAddress(address ?? undefined);

  if (!common) {
    return undefined;
  }

  return {
    address1: common.address1 ?? '',
    address2: common.address2 ?? '',
    city: common.city ?? '',
    state: normalizeStateCode(common.state, common.country),
    zip: common.zip ?? '',
    country: common.country ?? '',
    company_name: common.company_name ?? '',
    tax_number: common.tax_number ?? '',
  };
};

export const mapLinkShipping = (
  mode: TMultiKitAddressMode,
  overrideAddress: IOrgAddress | null,
  claimAddressIds: string[],
  isGroupShipmentEnabled = false,
): ILinkShippingCandidate => {
  if (mode === ShippingModeEnum.FixedAddress) {
    return {
      status: '',
      is_receiver_address_fixed: true,
      is_office_dropship_enabled: isGroupShipmentEnabled,
      receiver_fixed_address: mapLinkFixedAddress(overrideAddress),
    };
  }

  return {
    status: '',
    is_receiver_address_fixed: mode === ShippingModeEnum.FromList,
    is_office_dropship_enabled: isGroupShipmentEnabled,
    ...(claimAddressIds.length ? { org_address_ids: claimAddressIds } : {}),
  };
};

export const mapLinkNote = (customMessage: IEffectiveCreateSendCustomMessage): IAddNoteFormValues =>
  customMessage.enabled
    ? { [MESSAGE]: customMessage.message, [NOTE_TEMPLATE_ID]: customMessage.templateId }
    : { [MESSAGE]: '', [NOTE_TEMPLATE_ID]: null };

export const mapLinkEmail = (email: IEffectiveCreateSendEmail): Partial<IAddEmailFormValues> =>
  email.isOn && email.emailTemplateId
    ? {
        [EMAIL_TEMPLATE_ID]: email.emailTemplateId,
        [EMAIL_SUBJECT]: email.subject,
        [EMAIL_MESSAGE]: email.body,
        [RECEIVER_EMAIL]: '',
      }
    : {};

export const normalizeLinkSendOnBehalf = (
  sobEmail: string | null | undefined,
  userEmail: string | undefined,
): string | undefined => (sobEmail && sobEmail !== userEmail ? sobEmail : undefined);

export interface ILinkShippingSeed {
  mode: TMultiKitAddressMode;
  claimAddressIds: string[];
  overrideAddress: IOrgAddress | null;
}

export const DEFAULT_LINK_SHIPPING_SEED: ILinkShippingSeed = {
  mode: ShippingModeEnum.RecipientDecides,
  claimAddressIds: [],
  overrideAddress: null,
};

const filterActiveAddressIds = (addressIds: string[] | undefined, addressBook: IOrgAddress[] | undefined): string[] =>
  (addressIds ?? []).filter((uid) =>
    (addressBook ?? []).some((address) => address.uid === uid && address.status === OrgAddressFilterTypesEnum.Active),
  );

export const linkShippingSeedNeedsAddressBook = (campaign: ICampaign): boolean => {
  if (isDigitalBox(campaign)) {
    return false;
  }

  const { is_delayed_shipping, org_address_ids } = campaign.shipping_configuration || {};

  return Boolean(is_delayed_shipping && org_address_ids?.length && !org_address_ids.includes(CUSTOM));
};

export const computeLinkShippingSeed = (
  campaign: ICampaign,
  addressBook: IOrgAddress[] | undefined,
): ILinkShippingSeed => {
  if (isDigitalBox(campaign)) {
    return DEFAULT_LINK_SHIPPING_SEED;
  }

  const { is_receiver_address_fixed, is_delayed_shipping, org_address_ids } = campaign.shipping_configuration || {};

  if (!is_delayed_shipping) {
    return DEFAULT_LINK_SHIPPING_SEED;
  }

  const hasOneAddressRecord = org_address_ids?.length === 1;
  const hasCustomAddressRecord = org_address_ids?.includes(CUSTOM);

  if (is_receiver_address_fixed) {
    if (hasCustomAddressRecord) {
      return { mode: ShippingModeEnum.FixedAddress, claimAddressIds: [], overrideAddress: null };
    }

    if (hasOneAddressRecord) {
      const [addressId] = org_address_ids ?? [];
      const record = (addressBook ?? []).find(
        (address) => address.status === OrgAddressFilterTypesEnum.Active && address.uid === addressId,
      );

      return { mode: ShippingModeEnum.FixedAddress, claimAddressIds: [], overrideAddress: record ?? null };
    }

    return {
      mode: ShippingModeEnum.FromList,
      claimAddressIds: filterActiveAddressIds(org_address_ids, addressBook),
      overrideAddress: null,
    };
  }

  if (org_address_ids?.length) {
    return {
      mode: ShippingModeEnum.RecipientDecides,
      claimAddressIds: filterActiveAddressIds(org_address_ids, addressBook),
      overrideAddress: null,
    };
  }

  return DEFAULT_LINK_SHIPPING_SEED;
};
